import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { Subject, Subscription } from 'rxjs';
import { differenceInDays } from 'date-fns';
import { GraphTrendIconComponent } from '../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { GroupedBarChartComponent } from '../../../../../_shared/components/graphs/grouped-bar-chart/grouped-bar-chart.component';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { CostTrackingService } from '../../../services/cost-tracking.service';
import { FilterService } from '../../../../../_shared/services/filter.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { BudgetVsActualInsightsResponse } from '../../../models/cost-tracking-response.model';
import { WidgetFilterData } from '../../../../../_shared/models/filters/widget-filter-data.model';
import { DateInterval } from '../../../../../_shared/enums';
import { TimeSeriesGroupingHelper, GroupedChartData } from '../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { formatValue } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { calculateChartYTicks } from '../../../../../_shared/helpers/chart-y-ticks.helper';
import { BudgetedVsActualModalComponent } from '../../modals/budgeted-vs-actual-modal/budgeted-vs-actual-modal.component';
import { AuthService } from '../../../../../_shared/services/auth.service';
import { TranslocoService } from '@jsverse/transloco';
import { hasCompleteDateRange } from '../../../../../_shared/helpers/widget-filter.helper';
import { AccountDateFilterRequest } from '../../../../../_shared/models/account-date-filter-request.model';

@Component({
  selector: 'app-budgeted-vs-actual',
  standalone: true,
  imports: [CommonModule, GraphTrendIconComponent, GroupedBarChartComponent, EmptyStateComponent],
  templateUrl: './budgeted-vs-actual.component.html',
  styleUrl: './budgeted-vs-actual.component.scss'
})
export class BudgetedVsActualComponent implements OnInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
  private costTrackingService: CostTrackingService = inject(CostTrackingService);
  private filterService: FilterService = inject(FilterService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private modalService = inject(NgbModal);
  private cancelSubject: Subject<void> = new Subject<void>();
  private authService = inject(AuthService);

  currencySymbol: string = '';
  currencyCode: string = '';

  chartData: GroupedChartData[] = [];
  readonly barWidth = 12;
  readonly chartHeight = 165;
  private readonly defaultYTicks = [100, 80, 60, 40, 20, 0];
  yTicks: number[] = this.defaultYTicks;

  // Updated colors: Budgeted #3873C9, Actual #DCC2E3
  readonly subgroups = [
    { name: 'budgeted', legend: this.transloco.translate('costTracking.charts.budgetedCost'), color: '#3873C9' },
    { name: 'actual', legend: this.transloco.translate('costTracking.charts.actualCost'), color: '#DCC2E3' }
  ];

  title: string = inject(TranslocoService).translate('costTracking.charts.budgetedVsActual');
  isLoading: boolean = true;
  totalValue: number = 0;
  percentageChange: number = 0;
  widgetFilter: WidgetFilterData;

  private filterSubscription: Subscription;

  constructor() {
    this.filterSubscription = this.filterService.filterUpdated$.subscribe(async (filter) => {
      this.widgetFilter = filter;
      await this.loadData();
    });

    this.currencyCode = this.authService.getUserCurrencyCode();
    this.currencySymbol = this.authService.getUserCurrencySymbol();
  }

  ngOnInit(): void {
    this.widgetFilter = this.filterService.filter;
    void this.loadData();
  }

  ngOnDestroy(): void {
    this.filterSubscription?.unsubscribe();
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  private async loadData(): Promise<void> {
    if (!hasCompleteDateRange(this.widgetFilter)) return;

    this.isLoading = true;

    try {
      this.cancelSubject.next();

      const request: AccountDateFilterRequest = {
        accountIds: this.widgetFilter.accountIds ?? [],
        timeFrom: this.widgetFilter.startDate,
        timeTo: this.widgetFilter.endDate,
        interval: this.widgetFilter.interval,
      };

      const [summaryResponse, budgetedResponse, actualResponse] = await Promise.all([
        this.costTrackingService.getBudgetVsActualSummary(request, this.cancelSubject),
        this.costTrackingService.getBudgetedInsights(request, this.cancelSubject),
        this.costTrackingService.getActualInsights(request, this.cancelSubject)
      ]);

      const budgetedData = budgetedResponse.data ?? [];
      const actualData = actualResponse.data ?? [];
      const summary = summaryResponse.data;

      this.chartData = this.processResponses(budgetedData, actualData);
      this.yTicks = this.calculateYTicks(this.chartData);
      this.percentageChange = summary?.percentageChange ?? 0;
      this.totalValue = summary?.currentValue ?? 0;

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.chartData = [];
          this.isLoading = false;
        }
      });
    }
  }

  private processResponses(
    budgetedData: BudgetVsActualInsightsResponse[],
    actualData: BudgetVsActualInsightsResponse[]
  ): GroupedChartData[] {
    const daysDifference = differenceInDays(this.widgetFilter.endDate!, this.widgetFilter.startDate!);
    const groupingType = this.determineGroupingType(daysDifference, this.widgetFilter.interval);

    const datasets = [
      { data: budgetedData, label: 'budgeted' },
      { data: actualData, label: 'actual' }
    ];

    switch (groupingType) {
      case 'hourly': return TimeSeriesGroupingHelper.groupByHours(datasets);
      case 'daily': return TimeSeriesGroupingHelper.groupByDays(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!, 7);
      default: return TimeSeriesGroupingHelper.groupByWeeks(datasets, this.widgetFilter.startDate!, this.widgetFilter.endDate!);
    }
  }

  private determineGroupingType(daysDifference: number, interval: DateInterval): 'hourly' | 'daily' | 'weekly' {
    if (daysDifference === 0) return 'hourly';
    if (daysDifference <= 7) return 'daily';
    return 'weekly';
  }

  private calculateYTicks(chartData: GroupedChartData[]): number[] {
    if (!chartData?.length) return this.defaultYTicks;

    const allValues = chartData.flatMap(item => [
      Number(item['budgeted']) || 0,
      Number(item['actual']) || 0
    ]);

    return calculateChartYTicks(allValues, this.defaultYTicks);
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  isUptrendPositive(): boolean {
    // For budget tracking, being under budget (negative change) is positive
    return false;
  }

  formatValue(): string {
    return formatValue(this.totalValue, { currencySymbol: this.currencySymbol });
  }

  hasData(): boolean {
    if (!this.chartData || this.chartData.length === 0) return false;

    return this.chartData.some(item => {
      return Object.keys(item).some(key => {
        if (key === 'date' || key === 'originalTimestamp' || key === 'displayLabel' || key === 'tooltipDateTime') return false;
        const value = Number(item[key]) || 0;
        return value > 0;
      });
    });
  }

  openModal(): void {
    const modalOptions: NgbModalOptions = {
      centered: true,
      backdrop: 'static',
      size: 'xl',
    };

    this.modalService.open(BudgetedVsActualModalComponent, modalOptions);
  }
}